import { observer } from 'mobx-react-lite'
import React from 'react'

import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListSubheader from '@mui/material/ListSubheader'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'

import { Text } from '@mxenabled/mxui'

import {
  ANALYTICS_EVENTS,
  Drawer,
  useEvent,
  useGlobalCopyStore,
  useNetWorthStore,
} from '../../common'

import type { NetWorth } from '../../core/types/NetWorth'

import NetWorthListItem from './NetWorthListItem'
import GainsLossesDetail from './GainsLossesDetail'
import AssetsLiabilitiesDetail from './AssetsLiabilitiesDetail'

const NetWorthList = () => {
  const { net_worth: copy } = useGlobalCopyStore()
  const theme = useTheme()
  const { onEvent } = useEvent()

  const {
    assetsLiabilities,
    gainsLosses,
    hoveredData,
    netWorthChartData: chartData,
    selectedData,
    setHoveredIndex,
    setSelectedIndex,
  } = useNetWorthStore()

  const [isGlDrawerOpen, setIsGlDrawerOpen] = React.useState(false)
  const [isAlDrawerOpen, setIsAlDrawerOpen] = React.useState(false)

  const buildStyles = (lineItem: NetWorth) => {
    const isHovered = lineItem.x === hoveredData?.x
    const isSelected = lineItem.x === selectedData?.x

    const listItemStyles = {
      backgroundColor: 'inherit',
      borderBottom: 'inherit',
      color: 'inherit',
    }

    if (isHovered) {
      listItemStyles.color = isSelected ? 'inherit' : listItemStyles.color
      listItemStyles.backgroundColor = theme.palette.action.hover
    }

    if (isSelected) {
      listItemStyles.backgroundColor = theme.palette.primary.main
      listItemStyles.borderBottom = theme.palette.neutral.light
      listItemStyles.color = theme.palette.common.white
    }

    return listItemStyles
  }

  const handleListItemClick = (
    event: React.MouseEvent<SVGPathElement | HTMLDivElement, MouseEvent>,
    dataIndex: number,
  ) => {
    setSelectedIndex(dataIndex)
    if (event.currentTarget instanceof HTMLDivElement) {
      setIsGlDrawerOpen(true)
    }
  }

  const handleOpenALDrawer = () => {
    setIsAlDrawerOpen(true)
    onEvent(ANALYTICS_EVENTS.NET_WORTH_WIDGET_CLICK_ASSETS_LIABILITIES)
  }

  const handleMouseEnter = (dataIndex: number) => {
    setHoveredIndex(dataIndex)
  }

  const handleMouseLeave = () => {
    setHoveredIndex(-1)
  }

  return (
    <Stack direction="column" sx={{ width: 320 }}>
      <List>
        <ListSubheader sx={{ backgroundColor: 'transparent', mb: 8, mt: 12, px: 12 }}>
          <Text bold={true} component="h2" variant="body1">
            {copy.monthly_totals}
          </Text>
        </ListSubheader>
        <Divider />
        {chartData.map((dataPoint: NetWorth, index: number) => {
          return (
            <NetWorthListItem
              getStyles={buildStyles}
              isHovered={dataPoint.x === hoveredData?.x}
              isSelected={dataPoint.x === selectedData?.x}
              item={dataPoint}
              key={dataPoint.x}
              onItemClick={(event) => handleListItemClick(event, index)}
              onMouseEnter={() => handleMouseEnter(index)}
              onMouseLeave={() => handleMouseLeave()}
            />
          )
        })}
      </List>
      <Button
        onClick={() => handleOpenALDrawer()}
        sx={{ alignSelf: 'center', marginTop: 16, width: 'fit-content' }}
        variant="outlined"
      >
        {copy.view_assets_and_liabilities}
      </Button>

      <Drawer
        ariaLabelClose={'Close'}
        isOpen={isGlDrawerOpen}
        onClose={() => setIsGlDrawerOpen(false)}
        shouldShowHeaderBorders={false}
        shouldShowHeaderShadow={true}
        title={copy.gains_and_losses}
      >
        {gainsLosses && <GainsLossesDetail {...gainsLosses} />}
      </Drawer>
      <Drawer
        ariaLabelClose={'Close'}
        isOpen={isAlDrawerOpen}
        onClose={() => setIsAlDrawerOpen(false)}
        shouldShowHeaderBorders={false}
        shouldShowHeaderShadow={true}
        title={copy.assets_and_liabilities}
      >
        {assetsLiabilities && <AssetsLiabilitiesDetail {...assetsLiabilities} />}
      </Drawer>
    </Stack>
  )
}

export default observer(NetWorthList)
